/**
 * state/chat/ingest.ts — the two sinks for `chat.*` events, plus the
 * history replay that reuses them.
 *
 * The decision of WHAT a chat event does to a message list is made once,
 * in `reducer.applyChatEvent`. This module owns only the two ways to
 * write it down:
 *
 *   active cluster   → Solid's fine-grained `setState`, so a delta
 *                      touches one message instead of the whole array,
 *                      plus the live-session bookkeeping (pending-reply
 *                      flag, streaming-idle marker, window cap, the
 *                      work-* unarchive safety net).
 *   inactive cluster → the plain cached slice, which becomes visible
 *                      when the operator switches back.
 */

import { captureClusterEpoch } from '~/lib/swap-guard';
import type { DaemonClient, DaemonEvent } from '~/lib/daemon-client';
import { log } from '~/lib/log';
import { parseProgress } from '~/lib/turn-progress';
import { isDelegationEvent } from '~/lib/delegation';
import { state, setState, activeClusterId, isHydrating, setHydrating, clearPendingReply, clearLastDelta, clearTurnProgress, setTurnProgress, historyLoadIsCurrent, markStreamEnded, isStreamEnded } from './store';
import { applyChatEvent, applyPatchToList, restoreLiveTail } from './reducer';
import { getOrCreateSlice, recordActivity } from './cluster-slices';
import { noteFinishedWork } from './unread';
import { unarchiveConv } from './conv-actions';
import { setInitialPaging } from './paging';
import { INITIAL_PAGE, type ChatMsg } from './types';

const LIVE_TEXT_EVENTS = new Set<string>([
  'chat.user',
  'chat.assistant.delta',
  'chat.assistant.final',
]);

/**
 * Ingest one daemon event into the ACTIVE cluster's store. Idempotent —
 * the same stream_id replaces in place rather than appending a duplicate.
 */
export function ingestEvent(ev: DaemonEvent): void {
  const conv = typeof ev.conv === 'string' ? ev.conv : null;
  if (!conv) return;
  // py-1.11.0 — safety net: an archived work-* conv that starts talking
  // again un-archives locally. The daemon would normally emit
  // `conv.unarchived` first; this is cheap insurance.
  if (
    !isHydrating() &&
    state.archivedConvs[conv] &&
    conv.startsWith('work-') &&
    LIVE_TEXT_EVENTS.has(ev.type)
  ) {
    unarchiveConv(conv);
  }

  // FIN1 — remember which turns are over, so a late snapshot can't revive them.
  if (ev.type === 'chat.assistant.final' || ev.type === 'chat.error' || ev.type === 'chat.cancelled') {
    markStreamEnded(ev.stream_id);
  }
  const arr = state.convMap[conv] ?? [];
  const patch = applyChatEvent(arr, ev);
  if (patch.op === 'patch') setState('convMap', conv, patch.index, patch.fields);
  else if (patch.op === 'append') setState('convMap', conv, [...arr, patch.msg]);
  else if (patch.op === 'patch-append' || patch.op === 'set') setState('convMap', conv, applyPatchToList(arr, patch));

  if (ev.type === 'chat.assistant.delta') {
    // A delta with no stream_id can't be attributed to a bubble; it is
    // dropped whole, side effects included (a half-applied delta would
    // clear the "preparing" flag with nothing on screen to replace it).
    if (patch.op === 'none') return;
    // V86p — first assistant chunk is here; drop the "preparing" flag.
    clearPendingReply(conv);
    // V89.2 — stamp last-delta so the bubble's idle hint can tell when
    // the agent has gone quiet mid-turn.
    setState('lastDeltaTsByConv', conv, Date.now());
    return;
  }
  if (ev.type === 'chat.assistant.final') {
    // FIN1 — observability: every live final, and which bubble it closed.
    // Lands in the project's debug.jsonl (src: cockpit) via the log
    // transport, so a "stuck Working…" can be traced to a final that
    // never arrived vs one that arrived and matched nothing.
    if (!isHydrating()) {
      log.info('final applied', {
        conv,
        stream_id: typeof ev.stream_id === 'string' ? ev.stream_id : null,
        op: patch.op,
        still_streaming: (state.convMap[conv] ?? []).filter((m) => m.kind === 'assistant' && m.streaming).length,
      });
    }
    // V86p — a final without a prior delta also drops the pending flag.
    clearPendingReply(conv);
    clearLastDelta(conv);
    // LT-3 — the turn is over; stop describing what it was doing.
    clearTurnProgress(conv);
    return;
  }
  if (ev.type === 'chat.cancelled' || ev.type === 'chat.error') {
    // REL1 — a dead turn must also stop the "preparing…" spinner, or the
    // thread carries the explanation AND a bubble still claiming to be
    // working on it.
    clearPendingReply(conv);
    clearLastDelta(conv);
    clearTurnProgress(conv);
  }
}

/**
 * MP4 — route a chat event to the right cluster. Active → the reactive
 * store; inactive → the cached slice, so the operator sees the messages
 * on switch-back rather than a gap.
 */
export function ingestEventForCluster(clusterKey: string, ev: DaemonEvent): void {
  // MP5 — record activity regardless, so the rail's working slug and
  // unread dot react to background daemons too.
  recordActivity(clusterKey, ev);
  const evConv = typeof ev.conv === 'string' ? ev.conv : '';
  const summary = activeClusterId() === clusterKey
    ? state.convs[evConv]
    : getOrCreateSlice(clusterKey).convs[evConv];
  noteFinishedWork(clusterKey, ev, summary);
  if (activeClusterId() === clusterKey) {
    ingestEvent(ev);
    return;
  }
  const conv = typeof ev.conv === 'string' ? ev.conv : null;
  if (!conv) return;
  // FIN1 — a final for a project not on screen goes to its cached slice.
  if (ev.type === 'chat.assistant.final' || ev.type === 'chat.error' || ev.type === 'chat.cancelled') {
    markStreamEnded(ev.stream_id);
  }
  if (ev.type === 'chat.assistant.final') {
    log.info('final filed to background project', { project: clusterKey, active: activeClusterId(), conv, stream_id: ev.stream_id ?? null });
  }
  const slice = getOrCreateSlice(clusterKey);
  const list = slice.convMap[conv] ?? [];
  const patch = applyChatEvent(list, ev);
  if (patch.op === 'none') return;
  slice.convMap[conv] = applyPatchToList(list, patch);
}

/**
 * Lazy-load a page of messages for `conv` and seed `convMap[conv]`.
 * `ChatThread` calls this when a conv gains focus; older pages come from
 * `paging.loadEarlierMessages`. History replays through `ingestEvent`
 * with `hydrating` set, so the same upsert/dedup logic that powers live
 * streaming also seeds the backlog.
 */
export async function loadConvMessagesPage(
  client: DaemonClient,
  conv: string,
  opts: { before?: string; limit?: number } = {},
): Promise<{ has_more: boolean; oldest_ts: string }> {
  const atCluster = captureClusterEpoch();
  if (!historyLoadIsCurrent(atCluster, client.transport.projectId)) {
    log.warn('[swap-guard] history load with another project\'s client — skipped', { conv, client: client.transport.projectId, active: atCluster.key });
    return { has_more: false, oldest_ts: '' };
  }
  // PAGE1 — a reload (switch-back, watchdog) passed no limit and got the
  // daemon's default of 200 messages; every first page is INITIAL_PAGE.
  const res = await client.chatConvMessages(conv, { ...opts, limit: opts.limit ?? INITIAL_PAGE });
  if (!historyLoadIsCurrent(atCluster, client.transport.projectId)) {
    log.warn('[swap-guard] project switched while loading history — dropped', { conv, from: atCluster.key });
    return { has_more: false, oldest_ts: '' };
  }
  if (!res.ok) {
    log.warn('loadConvMessagesPage failed', { conv, status: res.status, body: res.body.slice(0, 200) });
    return { has_more: false, oldest_ts: '' };
  }
  // First page (no `before`): reset convMap so a stale entry from a prior
  // session doesn't double up — BUT keep any in-flight streaming bubble
  // (A-CONVWINDOW-01). Wiping it dropped a live message when a WS delta
  // beat the history load on a fresh refresh. buildStream re-sorts by ts
  // and extracts `live` separately, so keeping the tail here is safe.
  // LIVE2 (mf27, 2026-10-14) — the tail is set aside during the replay and
  // put back by its start time. Kept at the FRONT, it sat before the whole
  // page: every operator message after it read as "queued · merges into
  // next turn" (six answered messages stacked in yellow), and each old
  // ending in the page could close it.
  let liveTail: ChatMsg[] = [];
  if (!opts.before) {
    liveTail = (state.convMap[conv] ?? []).filter(
      (m) => m.kind === 'assistant' && m.streaming,
    );
    setState('convMap', conv, []);
  }
  setHydrating(true);
  try {
    for (const raw of res.data.messages) {
      const ev = raw as DaemonEvent;
      const t = typeof ev.type === 'string' ? ev.type : '';
      // REL1 — `chat.error` is in this list because the FIRST page is the
      // one the operator reads: a turn that died has to explain itself
      // when the thread is reopened, not only while the tab was watching.
      // (`paging.historyMsg` covers the older pages.)
      // BT-17 — `delegate.*` rebuild the delegation cards through the same
      // reducer, so a reopened thread shows them exactly as live.
      if (
        t !== 'chat.user' && t !== 'chat.assistant' && t !== 'chat.assistant.final'
        && t !== 'chat.cancelled' && t !== 'chat.error' && t !== 'chat.notice'
        && !isDelegationEvent(t)
      ) {
        continue;
      }
      // Older snapshots emit `chat.assistant` for finalised text; map it
      // so the reducer treats it as a closed turn.
      ingestEvent(t === 'chat.assistant' ? { ...ev, type: 'chat.assistant.final' } : ev);
    }
  } finally {
    setHydrating(false);
  }
  if (liveTail.length) setState('convMap', conv, restoreLiveTail(state.convMap[conv] ?? [], liveTail, isStreamEnded));
  setInitialPaging(conv, !!res.data.has_more, res.data.oldest_ts ?? '');
  return { has_more: res.data.has_more, oldest_ts: res.data.oldest_ts };
}

/**
 * LT-3 — `conv.progress`: the daemon's per-tick report of what a live
 * turn is doing (which tool it is inside and for how long, how long the
 * turn has run, how long it has been quiet).
 *
 * Kept out of `ingestConvEvent` deliberately: that path is gated on the
 * snapshot having hydrated, and this one must work the moment the socket
 * is up — a turn already running when the cockpit opens is exactly the
 * case the operator needs described.
 */
export function ingestProgressEvent(ev: Record<string, unknown>): void {
  const conv = typeof ev.conv === 'string' ? ev.conv : null;
  if (!conv) return;
  setTurnProgress(conv, parseProgress(ev, Date.now()));
}
